import { apiClient, ApiError } from './client';
import { endpoints } from './endpoints';

/**
 * Firebase web config, fetched at runtime rather than baked in at build time.
 *
 * Service workers cannot read `import.meta.env`, and a build without env files
 * silently produces a bundle with blank keys — so the API is the single source
 * of truth. The same values back the blade console's own push setup.
 */
export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string | null;
  vapidKey: string;
}

/** The subset the Firebase SDK's initializeApp() needs. */
export type FirebaseAppOptions = Omit<FirebaseWebConfig, 'vapidKey'>;

export async function getFirebaseConfig(): Promise<FirebaseWebConfig> {
  // This endpoint returns a bare object, not the {status, message, data}
  // envelope — it is shared verbatim with the blade console.
  const res = await apiClient.get<FirebaseWebConfig>(endpoints.firebaseConfig);
  const cfg = res.data;
  if (!cfg?.apiKey || !cfg?.projectId || !cfg?.messagingSenderId || !cfg?.appId) {
    throw new ApiError('Firebase is not configured on this environment', res.status, cfg);
  }
  return cfg;
}

/**
 * Registers this browser's FCM token against the signed-in user.
 *
 * The backend's FirebaseTokenService keeps a per-platform list, so a web token
 * survives mobile logins and vice versa. `X-App-Platform: web` is attached by
 * the shared request interceptor, which is what tags the entry.
 */
export async function updateFirebaseToken(token: string): Promise<void> {
  const res = await apiClient.post<{ status: 0 | 1; message?: string }>(
    endpoints.updateFirebaseToken,
    { firebase_token: token },
  );
  if (res.data.status === 0) {
    throw new ApiError(res.data.message || 'Failed to register for notifications', res.status, res.data);
  }
}
